import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Home, LayoutGrid, ShoppingCart } from 'lucide-react';
import { useCartStore, useCartTotals } from '../../store/cartStore';
import { settingsApi } from '../../lib/api';
import { cn } from '../../lib/utils';

interface NavItem {
    icon: React.ElementType;
    activeIcon?: React.ElementType;
    label: string;
    path?: string;
    action?: () => void;
    badge?: number;
}

export function BottomNavBar() {
    const location = useLocation();
    const openCart = useCartStore((state) => state.openCart);
    const { totalItems } = useCartTotals();

    // Fetch store settings for mobile nav configuration
    const { data: settings } = useQuery({
        queryKey: ['store-settings'],
        queryFn: settingsApi.get,
        staleTime: 1000 * 60 * 10, // 10 minutes
    });

    const navItems: NavItem[] = [
        { icon: Home, label: 'Accueil', path: '/' },
        { icon: LayoutGrid, label: 'Catégories', path: '/shop' },
        { icon: ShoppingCart, label: 'Panier', action: openCart, badge: totalItems },
    ];

    // Check if mobile nav is disabled
    const mobileNavEnabled = settings?.mobileNavEnabled ?? true;
    const mobileNavStyle = settings?.mobileNavStyle ?? 'floating';

    // Don't render if disabled
    if (!mobileNavEnabled) {
        return null;
    }

    // Determine nav bar styles based on setting
    const isFloating = mobileNavStyle === 'floating';

    return (
        <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden safe-area-bottom">
            {/* Navigation bar - floating or fixed style */}
            <div className={cn(
                "bg-white",
                isFloating 
                    ? "mx-3 mb-2 rounded-2xl shadow-[0_-2px_20px_rgba(0,0,0,0.08)] border border-gray-100/80" 
                    : "border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
            )}>
                <div className="flex items-center justify-around h-[68px] px-1">
                    {navItems.map((item) => {
                        const isActive = item.path 
                            ? location.pathname === item.path || (item.path === '/shop' && location.pathname.startsWith('/shop'))
                            : false;
                        const Icon = item.icon;

                        const content = (
                            <motion.div 
                                className="relative flex flex-col items-center justify-center py-2 px-3"
                                whileTap={{ scale: 0.92 }}
                            >
                                {/* Active background pill */}
                                {isActive && (
                                    <motion.div
                                        layoutId="navActiveBackground"
                                        className="absolute inset-0 bg-primary-50 rounded-xl"
                                        initial={false}
                                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                                    />
                                )}
                                
                                <div className="relative z-10">
                                    <Icon 
                                        className={cn(
                                            "w-[22px] h-[22px] transition-all duration-200",
                                            isActive 
                                                ? "text-primary-500 stroke-[2.5px]" 
                                                : "text-gray-400 stroke-[1.5px]"
                                        )} 
                                        fill={isActive ? "currentColor" : "none"}
                                        strokeWidth={isActive ? 0 : 1.5}
                                    />
                                    {item.badge && item.badge > 0 && (
                                        <motion.span
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            className="absolute -top-1 -right-2 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold text-white bg-primary-500 rounded-full px-1 shadow-sm"
                                        >
                                            {item.badge > 99 ? '99+' : item.badge}
                                        </motion.span>
                                    )}
                                </div>
                                <span 
                                    className={cn(
                                        "text-[10px] mt-1 font-semibold transition-colors relative z-10",
                                        isActive ? "text-primary-600" : "text-gray-400"
                                    )}
                                >
                                    {item.label}
                                </span>
                            </motion.div>
                        );

                        if (item.action) {
                            return (
                                <button
                                    key={item.label}
                                    onClick={item.action}
                                    className="flex-1 flex items-center justify-center touch-manipulation"
                                    aria-label={item.label}
                                >
                                    {content}
                                </button>
                            );
                        }

                        return (
                            <Link
                                key={item.label}
                                to={item.path!}
                                className="flex-1 flex items-center justify-center touch-manipulation"
                                aria-label={item.label}
                            >
                                {content}
                            </Link>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
}
